import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  effect,
  EventEmitter,
  HostListener,
  Input,
  input,
  OnInit,
  Output,
  signal,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { ContentTypes, Media, Themes } from '../../enums/enums'
import { Dictionary } from '../../models/main/dictionary'
import { Figure } from '../../models/main/figure'
import { Note } from '../../models/main/note'
import { Pair } from '../../models/main/pair'
import { Quiz } from '../../models/main/quiz'
import { Word } from '../../models/main/word'
import { HighlightWordPipe } from '../../pipes/highlight-word.pipe'
import { DdcBookmarkComponent } from '../ddc-bookmark/ddc-bookmark.component'
import { DdcIconFunctionComponent } from '../ddc-delete/ddc-icon-function.component'
import { DdcDesktopDialogComponent } from '../ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcElementCountComponent } from '../ddc-element-count/ddc-element-count.component'
import { DdcExpandBtnComponent } from '../ddc-expand-btn/ddc-expand-btn.component'
import { DdcLikeComponent } from '../ddc-like/ddc-like.component'
import { DdcLingoFunctionComponent } from '../ddc-lingo-function/ddc-lingo-function.component'
import { DdcMobileSheetComponent } from '../ddc-mobile-sheet/ddc-mobile-sheet.component'
import { DdcProfilePicComponent } from '../ddc-profile-pic/ddc-profile-pic.component'
import { DdcThreeDotsComponent } from '../ddc-three-dots/ddc-three-dots.component'

export type DdcContent = Word | Pair | Dictionary | Note | Quiz | Figure

@Component({
  selector: 'ddc-content-card',
  templateUrl: './ddc-content-card.component.html',
  styleUrl: './ddc-content-card.component.scss',
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcProfilePicComponent,
    DdcThreeDotsComponent,
    DdcLikeComponent,
    DdcBookmarkComponent,
    DdcExpandBtnComponent,
    HighlightWordPipe,
    DdcElementCountComponent,
    DdcIconFunctionComponent,
    DdcLingoFunctionComponent,
  ],
})
export class DdcContentCardComponent implements OnInit {
  content = input<DdcContent>(undefined)
  dynamicRoute = input<string>(undefined)

  @Input() subscription: string = 'Free Tier'
  @Output() clicked = new EventEmitter<void>()
  @Output() editClicked = new EventEmitter<void>()
  @Output() deleteClicked = new EventEmitter<void>()
  @Output() bookmarkChanged = new EventEmitter<boolean>()

  _isMobile: boolean = false
  _theme$: Observable<string>

  protected readonly isMine = computed(() => {
    const content = this.content()
    return content && content.is_mine
  })

  word = computed<Word | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'word' in content &&
      'language' in content &&
      !('word_1' in content) && // Not a Pair
      !('title' in content) // Not a Dictionary
    ) {
      // Convert plain object to Word instance if needed
      const wordInstance = Object.assign(new Word(), content)
      return wordInstance
    } else {
      return undefined
    }
  })

  pair = computed<Pair | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'word_1' in content &&
      'word_2' in content &&
      !('title' in content) // Not a Dictionary
    ) {
      const pairInstance = Object.assign(new Pair(), content)
      return pairInstance
    } else {
      return undefined
    }
  })

  dictionary = computed<Dictionary | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'title' in content &&
      'words_count' in content &&
      'pairs_count' in content &&
      !('word' in content) && // Not a Word
      !('word_1' in content) && // Not a Pair
      !('word_2' in content) // Not a Pair
    ) {
      const dictionaryInstance = Object.assign(new Dictionary(), content)
      return dictionaryInstance
    } else {
      return undefined
    }
  })

  note = computed<Note | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'title' in content &&
      'text' in content &&
      !('word' in content) && // Not a Word
      !('word_1' in content) && // Not a Pair
      !('word_2' in content) && // Not a Pair
      !('description' in content) // Not a Dictionary
    ) {
      const noteInstance = Object.assign(new Note(), content)
      return noteInstance
    } else {
      return undefined
    }
  })

  quiz = computed<Quiz | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'title' in content &&
      'questions_count' in content &&
      !('word' in content) && // Not a Word
      !('word_1' in content) && // Not a Pair
      !('word_2' in content) // Not a Pair
    ) {
      const quizInstance = Object.assign(new Quiz(), content)
      return quizInstance
    } else {
      return undefined
    }
  })

  figure = computed<Figure | undefined>(() => {
    const content = this.content()
    if (
      content &&
      'title' in content &&
      'figure_pic' in content &&
      !('word' in content) && // Not a Word
      !('word_1' in content) && // Not a Pair
      !('word_2' in content) // Not a Pair
    ) {
      const figureInstance = Object.assign(new Figure(), content)
      return figureInstance
    } else {
      return undefined
    }
  })

  contentTypeID = computed(() => {
    if (this.word()) {
      return ContentTypes.WORD
    } else if (this.pair()) {
      return ContentTypes.PAIR
    } else if (this.dictionary()) {
      return ContentTypes.DIC
    } else if (this.note()) {
      return ContentTypes.NOTE
    } else if (this.quiz()) {
      return ContentTypes.QUIZ
    } else if (this.figure()) {
      return ContentTypes.FIGURE
    } else {
      return null
    }
  })

  private windowWidth = signal<number>(window.innerWidth)
  hasProfileInfo = computed(() => this.windowWidth() > 1000)
  isMobile = computed(() => this.windowWidth() < 650)

  private showDef = signal<boolean>(false)
  private showExp = signal<boolean>(false)
  private showDesc = signal<boolean>(false)

  showDefinition = computed(() => this.showDef())
  showExample = computed(() => this.showExp())
  showDescription = computed(() => this.showDesc())

  isLightTheme = signal<boolean>(true)
  isDarkTheme = signal<boolean>(false)
  isPaperTheme = signal<boolean>(false)

  constructor(
    public themeService: ThemeService,
    private router: Router,
    private store: Store<AppState>,
    public dialog: MatDialog,
  ) {
    effect(() => {
      let theme: Themes
      this.themeService.theme$.subscribe(t => (theme = t))
      this.isLightTheme.set(theme === Themes.LIGHT)
      this.isDarkTheme.set(theme === Themes.DARK)
      this.isPaperTheme.set(theme === Themes.PAPER)
    })

    this._theme$ = this.store.select(getTheme)
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.windowWidth.set(window.innerWidth)
    this._isMobile = event.target.innerWidth < 1224
  }

  ngOnInit(): void {
    this.windowWidth.set(window.innerWidth)
  }

  toggleContent(): void {
    const newState = !(this.showDefinition() || this.showExample() || this.showDescription())
    this.showDef.set(newState)
    this.showExp.set(newState)
    this.showDesc.set(newState)
  }

  cardClick(): void {
    if (this.dictionary() !== undefined && this.dynamicRoute() !== undefined) {
      this.router.navigate([this.dynamicRoute()])
    } else {
      this.clicked.emit()
    }
  }

  cardEditClick(): void {
    this.editClicked.emit()
  }

  handleBookmarkChanged(event: any): void {
    this.bookmarkChanged.emit(event)
  }

  cardDeleteClick(): void {
    this.deleteClicked.emit()
  }

  routeToFlashCardsModePage(): void {
    if (this.dictionary() !== undefined) {
      this.router.navigate(['/flashcard-mode', this.dictionary()!.id])
    }
  }

  getFigurePicUrl(url: string | undefined): string {
    if (!url) return ''
    if (url.startsWith('http')) {
      // Extract path after /media/ and use proxied assets path
      const mediaIndex = url.indexOf('/media/')
      return mediaIndex !== -1 ? Media.ASSETS + url.slice(mediaIndex) : url
    }
    return Media.ASSETS + url
  }

  showUpgradeDialog(): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()

      // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
      let themeClass = ''
      this._theme$
        .subscribe(theme => {
          themeClass = theme
        })
        .unsubscribe()
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = { type: 'upgrade' }
      const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type: 'upgrade' }
      const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  showGeneratePdfDialog(): void {
    if (this._isMobile) {
      const dialogConfig = new MatDialogConfig()

      // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
      let themeClass = ''
      this._theme$
        .subscribe(theme => {
          themeClass = theme
        })
        .unsubscribe()
      dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

      dialogConfig.position = { bottom: '0' }
      dialogConfig.width = '100%'
      dialogConfig.height = '80vh'
      dialogConfig.maxWidth = '100vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.backdropClass = 'mobile-sheet-backdrop'
      dialogConfig.enterAnimationDuration = '300ms'
      dialogConfig.exitAnimationDuration = '200ms'
      dialogConfig.data = { type: 'generate_pdf', element: this.dictionary() }
      const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type: 'generate_pdf', element: this.dictionary() }
      const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }
}
